import { ScrollPanel } from 'primereact/scrollpanel';
import { Tag } from 'primereact/tag';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import FilterType from '../../models/FilterType';
import ImageModal from '../ImageModal/ImageModal';
import Product from '../../models/Product';
import { AuthService } from '../../services/auth.service';
import './ProductTableList.css';
import { ProductImage } from './ProductImage';
import { formatCurrency } from '../../utils/formatCurrency';

interface Props {
  filters: FilterType;
  products: Product[];
}

export const ProductTableList: FC<Props> = ({ products }) => {
  const { increaseCartQuantity } = useShoppingCart();
  const { t } = useTranslation();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageForModal, setImageForModal] = useState('');

  const [imageSources, setImageSources] = useState<Record<string, string>>(
    Object.fromEntries(
      products.map((product) => [product.code, '/placeholder.jpg']),
    ),
  );

  const handleImageLoad = (code: string, url: string) => {
    setImageSources((prev) => ({
      ...prev,
      [code]: url,
    }));
  };

  const save = (
    code: string,
    nameProduct: string,
    price: number | null | undefined,
    imgUrl: string,
    lowest: number,
    multiple: number,
  ) => {
    increaseCartQuantity(
      code,
      nameProduct,
      price ?? 0,
      imgUrl,
      lowest,
      multiple,
    );
  };

  const handleImageClick = (imgUrl: string) => {
    setIsImageModalOpen(true);
    setImageForModal(imgUrl);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setImageForModal('');
  };

  return (
    <div style={{ paddingLeft: '5%' }}>
      <ScrollPanel style={{ width: '100%', height: 'calc(100vh - 50px)' }}>
        <table className="table-products">
          <tbody>
            {products.map((product) => {
              return (
                <tr key={product.code}>
                  <td>
                    <div className="tooltip">
                      <ProductImage
                        code={product.code}
                        alt={product.nameProduct}
                        onClick={(url) => handleImageClick(url)}
                        onImageLoad={handleImageLoad}
                      />
                    </div>
                  </td>
                  <td>{product.code}</td>
                  <td>
                    <strong>{product.nameProduct.toLocaleLowerCase()}</strong>
                  </td>
                  <td>
                    <div className="tags-container">
                      {product.availableWW > 0 ? (
                        <Tag
                          className="bg-green-100"
                          icon="pi pi-check"
                          severity="warning"
                          value={t('card_component.tag.available')}
                        />
                      ) : (
                        <Tag
                          className="bg-red-100"
                          icon="pi pi-times"
                          severity="warning"
                          value={t('card_component.tag.unavailable')}
                        />
                      )}
                      {product.availableWW > 0 && product.isOnDemand && (
                        <Tag
                          className="bg-orange-100"
                          icon="pi pi-exclamation-circle"
                          severity="warning"
                          data-pr-tooltip={t('card_component.tag.on_demand')}
                          value={t('card_component.tag.on_demand')}
                        />
                      )}
                      {product.availableWW > 0 &&
                        product.isEnabled === 'Agotar' && (
                          <Tag
                            className="bg-blue-100"
                            icon="pi pi-tag"
                            severity="warning"
                            value={t('card_component.tag.end_stock')}
                          />
                        )}
                    </div>
                  </td>
                  <td>
                    <strong>
                      {product.price === 0 ||
                      product.price === null ||
                      product.price === undefined
                        ? '-'
                        : formatCurrency(product.price)}
                    </strong>
                  </td>
                  <td>
                    <button
                      className="meso-button"
                      onClick={() =>
                        save(
                          product.code,
                          product.nameProduct,
                          product.price,
                          imageSources[product.code],
                          product.lowest ?? 1,
                          product.multiple ?? 1,
                        )
                      }
                      disabled={
                        AuthService.GetRole() === 'Marketing'
                          ? true
                          : false ||
                            product.availableWW <= 0 ||
                            product.price === 0 ||
                            product.price === null ||
                            product.price === undefined
                      }
                    >
                      {t('card_component.add_to_cart_btn')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollPanel>
      {isImageModalOpen && (
        <ImageModal imgUrl={imageForModal} onClose={handleCloseImageModal} />
      )}
    </div>
  );
};
